import { useLayoutEffect, useRef } from "react";

const ZOOM_STEP = 0.01;
const MAX_SETTLE_STEPS = 8;
const SUBPIXEL_TOLERANCE = 0.01;
const CONTENT_MUTATIONS = {
  characterData: true,
  childList: true,
  subtree: true,
};

interface FitTarget {
  container: HTMLElement;
  content: HTMLElement;
  minFontSizePx: number;
}

interface NaturalSize {
  width: number;
  fontSize: number;
}

const queuedTargets = new Set<FitTarget>();
let isMicrotaskQueued = false;
let isFrameQueued = false;

const useFitText = <Container extends HTMLElement, Content extends HTMLElement>(
  minFontSizePx: number,
  fontKey?: string
) => {
  const containerRef = useRef<Container>(null);
  const contentRef = useRef<Content>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const target = { container, content, minFontSizePx };
    fitBeforePaint(target);
    return observeTarget(target);
  }, [minFontSizePx, fontKey]);

  return { containerRef, contentRef };
};

export default useFitText;

const observeTarget = (target: FitTarget) => {
  const onLayoutChange = () => fitNextFrame(target);
  const resizeObserver = new ResizeObserver(onLayoutChange);
  resizeObserver.observe(target.container);
  if (target.container.parentElement)
    resizeObserver.observe(target.container.parentElement);
  const mutationObserver = new MutationObserver(() => fitBeforePaint(target));
  mutationObserver.observe(target.content, CONTENT_MUTATIONS);
  document.fonts?.addEventListener("loadingdone", onLayoutChange);
  return () => {
    queuedTargets.delete(target);
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    document.fonts?.removeEventListener("loadingdone", onLayoutChange);
  };
};

const fitBeforePaint = (target: FitTarget) => {
  queuedTargets.add(target);
  if (isMicrotaskQueued) return;
  isMicrotaskQueued = true;
  queueMicrotask(() => {
    isMicrotaskQueued = false;
    flushQueuedTargets();
  });
};

const fitNextFrame = (target: FitTarget) => {
  queuedTargets.add(target);
  if (isFrameQueued) return;
  isFrameQueued = true;
  requestAnimationFrame(() => {
    isFrameQueued = false;
    flushQueuedTargets();
  });
};

const flushQueuedTargets = () => {
  const targets = [...queuedTargets].filter(
    ({ container }) => container.isConnected
  );
  queuedTargets.clear();
  if (targets.length) fitTargets(targets);
};

const fitTargets = (targets: FitTarget[]) => {
  targets.forEach(({ content }) => setZoom(content, 1));
  const availableWidths = targets.map(
    ({ container }) => container.getBoundingClientRect().width
  );
  const naturalSizes = measureNaturalSizes(
    targets.map(({ content }) => content)
  );
  const zooms = targets.map((target, index) =>
    getFittingZoom(target, availableWidths[index], naturalSizes[index])
  );
  targets.forEach((target, index) => setZoom(target.content, zooms[index]));
  settleOverflow(
    targets.map((target, index) => ({
      content: target.content,
      zoom: zooms[index],
      minZoom: getMinZoom(target, naturalSizes[index]),
    }))
  );
};

const settleOverflow = (
  fits: { content: HTMLElement; zoom: number; minZoom: number }[]
) => {
  let overflowing = fits.filter(canShrink);
  for (let step = 0; step < MAX_SETTLE_STEPS && overflowing.length; step++) {
    overflowing.forEach((fit) => {
      fit.zoom = stepDownZoom(fit.zoom, fit.minZoom);
      setZoom(fit.content, fit.zoom);
    });
    overflowing = overflowing.filter(canShrink);
  }
};

const canShrink = ({
  content,
  zoom,
  minZoom,
}: {
  content: HTMLElement;
  zoom: number;
  minZoom: number;
}) => zoom > minZoom && overflows(content);

/**
 * Any overflow, even under a pixel, ends the text in an ellipsis, and
 * scrollWidth rounds a sub-pixel one away: the laid-out text is measured.
 */
export const overflows = (content: HTMLElement) => {
  if (content.scrollWidth > content.clientWidth) return true;
  const text = document.createRange();
  text.selectNodeContents(content);
  const textWidth = text.getBoundingClientRect?.().width ?? 0;
  return textWidth > content.getBoundingClientRect().width + SUBPIXEL_TOLERANCE;
};

export const stepDownZoom = (zoom: number, minZoom: number) =>
  Math.max(minZoom, Math.round((zoom - ZOOM_STEP) / ZOOM_STEP) * ZOOM_STEP);

const getMinZoom = (
  { minFontSizePx }: Pick<FitTarget, "minFontSizePx">,
  { fontSize }: NaturalSize
) => Math.min(1, minFontSizePx / fontSize);

const measureNaturalSizes = (elements: HTMLElement[]): NaturalSize[] => {
  const widths = elements.map((element) => element.style.width);
  elements.forEach((element) => (element.style.width = "max-content"));
  const sizes = elements.map((element) => ({
    width: element.getBoundingClientRect().width,
    fontSize: parseFloat(getComputedStyle(element).fontSize),
  }));
  elements.forEach((element, index) => (element.style.width = widths[index]));
  return sizes;
};

export const getFittingZoom = (
  { minFontSizePx }: Pick<FitTarget, "minFontSizePx">,
  availableWidth: number,
  { width, fontSize }: NaturalSize
) => {
  if (!availableWidth || width <= availableWidth) return 1;
  const minZoom = getMinZoom({ minFontSizePx }, { width, fontSize });
  return Math.max(minZoom, roundDown(availableWidth / width));
};

const roundDown = (zoom: number) => Math.floor(zoom / ZOOM_STEP) * ZOOM_STEP;

const setZoom = (element: HTMLElement, zoom: number) => {
  const value = zoom === 1 ? "" : String(zoom);
  if (element.style.zoom !== value) element.style.zoom = value;
};
